import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaImage, FaPlus, FaTrash, FaMapLocationDot } from "react-icons/fa6";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { PopService } from "../../../../services/PopService";
import { SelectNas } from "../../../../components/forms/selects/SelectNas";
import { Header } from "../../../../components/Header";
import { handleDetectGPS } from "../../../../utilities/leftjs/handleDetectGps";
import { FiCrosshair, FiLoader, FiTrash2 } from "react-icons/fi";
import { getLocationDetail } from "../../../../utilities/leftjs/getLocationDetail";
import { TextAreaDefault } from "../../../../components/forms/TextAreaDefault";
import { InputDefault } from "../../../../components/forms/InputDefault";
import { MapSync } from "../../../../utilities/leftjs/MapSync";

const CARD_STYLE = {
  background: "rgba(15, 23, 42, 0.5)",
  border: "1px solid rgba(51, 65, 85, 0.3)",
  backdropFilter: "blur(12px)",
};

const INPUT_STYLE = "w-full px-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all";

const DefaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// Reverse Geocoding Helper
const fetchAddressFromCoords = async (lat, lng) => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
    const response = await fetch(url, {
      headers: { "Accept": "application/json", "User-Agent": "DanelaApp/1.0" }
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.display_name || null;
  } catch (error) {
    console.error("Gagal mengambil alamat:", error);
    return null;
  }
};

// --- SUB-COMPONENTS (MAP) ---

const MapMarker = ({ position }) => {
  return position ? <Marker position={[position.lat, position.lng]} icon={DefaultIcon} /> : null;
};

const MapController = ({ data, setData }) => {
  const map = useMap();

  const handleUpdateLocation = async (lat, lng) => {
    setData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
    map.flyTo([lat, lng], 17);

    const address = await fetchAddressFromCoords(lat, lng);
    if (address) {
      setData((prev) => ({ ...prev, address: address }));
    }
  };

  useMapEvents({
    click(e) {
      handleUpdateLocation(e.latlng.lat, e.latlng.lng);
    },
  });

  const handleLocate = () => {
    if (!navigator.geolocation) return alert("Browser tidak mendukung Geolocation");
    map.locate({ setView: false, maxZoom: 16 });
    map.once("locationfound", async (e) => {
      handleUpdateLocation(e.latlng.lat, e.latlng.lng);
    });
    map.once("locationerror", () => alert("Gagal mendapatkan lokasi. Pastikan GPS aktif."));
  };

  return (
    <>
      <MapMarker position={{ lat: data.latitude, lng: data.longitude }} />
      <button
        type="button"
        onClick={handleLocate}
        className="absolute bottom-6 right-6 z-[1000] bg-slate-800 text-cyan-400 p-3 rounded-full shadow-2xl border border-slate-600 hover:bg-cyan-600 hover:text-white transition-all active:scale-90 flex items-center justify-center"
        title="Deteksi Lokasi Saya"
      >
        <FaMapLocationDot size={20} />
      </button>
    </>
  );
};

// --- MAIN COMPONENT ---

export const PopCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true); // State untuk Sidebar

  const defaultCenter = { lat: -6.17511, lng: 106.827153 };

  const [data, setData] = useState({
    name: "",
    description: "",
    address: "",
    longitude: defaultCenter.lng,
    latitude: defaultCenter.lat,
    images: [],
  });

  // Handlers
  const handleChange = (e) => {
    const { id, value } = e.target;
    setData({ ...data, [id]: value });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 3) return alert("Maksimal 3 gambar.");
    setData({ ...data, images: files });
    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
  };


  const [loadingGps, setLoadingGps] = useState(false);
  const [fetchingAddress, setFetchingAddress] = useState(false);
  const DEFAULT_CENTER = { lat: -6.17511, lng: 106.827153 };
  const [gpsInput, setGpsInput] = useState(`${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}`);
  const [gpsError, setGpsError] = useState(null);

  const handleGpsInputChange = (e) => {
    const value = e.target.value;
    setGpsInput(value);

    // Parse string "lat, lng" menjadi angka
    const parts = value.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());

      // Validasi apakah angka yang valid
      if (!isNaN(lat) && !isNaN(lng)) {
        setData((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
        }));
        // Reset error jika user berhasil input manual
        setGpsError(null);
      }
    }
  };

  const handleLocationUpdate = async (lat, lng) => {
    setData((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
    }));

    // 3. Fetch Alamat (Reverse Geocoding)
    setFetchingAddress(true);
    try {
        const locationData = await getLocationDetail(lat, lng);
        if (locationData && locationData.displayName) {
            setData((prev) => ({
                ...prev,
                address: locationData.displayName,
            }));
        }
    } catch (err) {
        console.error("Gagal mengambil alamat:", err);
    } finally {
        setFetchingAddress(false);
    }
};
  


  // Fungsi untuk deteksi GPS dengan error handling lengkap
  const safeDetectGps = async () => {
    setLoadingGps(true);
    setGpsError(null);

    try {
      await handleDetectGPS({
        setLoading: setLoadingGps,
        handleLocationUpdate: handleLocationUpdate
      });
    } catch (error) {
      console.warn("GPS tidak tersedia atau gagal, menggunakan lokasi default:", error.message || error);

      let errorMessage = "GPS tidak tersedia";
      if (error.code === 1) {
        errorMessage = "Akses lokasi ditolak oleh browser";
      } else if (error.code === 2) {
        errorMessage = "Lokasi tidak tersedia saat ini";
      } else if (error.code === 3) {
        errorMessage = "Waktu habis saat mendeteksi lokasi";
      } else if (error.message?.includes("permission")) {
        errorMessage = "Izin akses lokasi belum diberikan";
      }

      setGpsError(errorMessage);

      setData((prev) => ({
        ...prev,
        latitude: DEFAULT_CENTER.lat,
        longitude: DEFAULT_CENTER.lng,
      }));
      // Update input string juga
      setGpsInput(`${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}`);
    } finally {
      setLoadingGps(false);
    }
  };

  // Deteksi GPS saat komponen mount
  useEffect(() => {
    safeDetectGps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await PopService.create({ data: data });
      navigate("/dashboard/master-data/pop");
    } catch (error) {
      alert("Gagal menyimpan data.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Tambah Data POP - DANELA NET</title>
        <meta name="description" content="Tambah data Point of Presence baru." />
      </Helmet>

      <div>
        {/* HEADER */}
        <Header showToggleSidebar={false}>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-700/50 rounded-full transition-all text-slate-400 hover:text-white">
              <FaChevronLeft />
            </button>
            <div>
              <h2 className="text-base md:text-xl font-bold text-gray-100">Tambah Data POP</h2>
              <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">Form Input POP Baru</p>
            </div>
          </div>
        </Header>

        <main className="p-4 lg:p-6 space-y-6">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">

            {/* GRID ATAS: INFO & SPLITTER */}
            <div className="grid grid-cols-10 gap-6">

              {/* INFORMASI DASAR */}
              <div className="lg:col-span-5 rounded-2xl p-5 lg:p-6" style={CARD_STYLE}>
                <h3 className="text-xs font-black uppercase text-slate-400 mb-5 tracking-[0.2em] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500"></span>
                  Informasi Dasar
                </h3>

                <div className="flex flex-col gap-4">
                  {/* Nama & Port */}

                  <InputDefault
                    id="name"
                    label="Nama POP"
                    type="text"
                    placeholder="Cth: POP GADING"
                    value={data.name}
                    onChange={handleChange}
                    className={INPUT_STYLE}
                  />

                  {/* Deskripsi / Alamat */}
                  <TextAreaDefault
                    id="description"
                    label="Deskripsi"
                    placeholder="Masukan deskripsi atau keterangan..."
                    value={data.description}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* FOTO */}
              <div className="lg:col-span-5 rounded-2xl p-5 lg:p-6 flex flex-col h-full" style={CARD_STYLE}>
                <h3 className="text-xs font-black uppercase text-slate-400 mb-5 tracking-[0.2em]">Dokumentasi Foto</h3>
                <div className="flex flex-wrap gap-4">
                  {imagePreviews.map((src, i) => (
                    <div key={i} className="w-28 h-28 rounded-xl overflow-hidden border border-slate-700 shadow-sm relative group">
                      <img src={src} className="w-full h-full object-cover" alt="Preview" />
                    </div>
                  ))}

                  {[...Array(Math.max(0, 3 - imagePreviews.length))].map((_, i) => (
                    <div key={i} className="w-28 h-28 rounded-xl bg-slate-800/30 border border-slate-700/50 flex items-center justify-center">
                      <FaImage className="text-slate-700" size={24} />
                    </div>
                  ))}
                </div>
                <label className="w- border-2 border-dashed border-slate-600 rounded-xl flex items-center justify-center cursor-pointer hover:bg-slate-700/30 hover:border-cyan-500/50 transition-all group mt-5 gap-3 py-2 ">
                  <FaPlus className="text-slate-500 group-hover:text-cyan-400 transition-colors" />
                  <span className="text-[9px] font-black text-slate-400 uppercase">Upload</span>
                  <input type="file" className="hidden" multiple accept="image/*" onChange={handleImageChange} />
                </label>
              </div>

            </div>

            {/* Location */}
            <div className="rounded-2xl p-1 overflow-hidden border border-slate-700/50" style={{ background: "rgba(15, 23, 42, 0.5)" }}>
              <div className="p-4 border-b border-slate-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2">
                  <FaMapLocationDot className="text-cyan-500" /> Peta Lokasi
                </h3>
                <div className="flex gap-4 text-xs font-mono">
                  <span className="text-slate-500">Lat: <span className="text-cyan-400">{data.latitude.toFixed(5)}</span></span>
                  <span className="text-slate-500">Lng: <span className="text-cyan-400">{data.longitude.toFixed(5)}</span></span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-3 py-2">
                <div className="">

                  <div className="mt-5 flex flex-col gap-3">

                    {/* ============================================ */}
                    {/* INPUT GPS GABUNGAN - FORMAT: lat, lng */}
                    {/* ============================================ */}
                    <div className="relative">
                      <label
                        htmlFor="gps-input"
                        className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5"
                      >
                        GPS
                      </label>
                      <div className="relative">
                        <input
                          id="gps-input"
                          type="text"
                          inputMode="decimal"
                          placeholder="-6.17511, 106.827153"
                          value={gpsInput}
                          onChange={handleGpsInputChange}
                          className="w-full bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all font-mono pr-20"
                          required
                        />
                        <button
                          type="button"
                          onClick={safeDetectGps}
                          disabled={loadingGps}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-lg text-cyan-400 hover:bg-cyan-500/10 transition-all disabled:opacity-50"
                          title="Deteksi GPS"
                        >
                          {loadingGps ? <FiLoader className="animate-spin text-sm" /> : <FiCrosshair className="text-sm" />}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1">Format: latitude, longitude</p>
                    </div>

                    <TextAreaDefault
                      id="address"
                      label="Detail Alamat"
                      placeholder="Alamat akan terisi otomatis..."
                      value={data.address}
                      onChange={handleChange}
                    />

                    {/* Pesan Error GPS (jika ada) */}
                    {gpsError && (
                      <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <span className="text-amber-400 text-[11px] leading-relaxed">
                          ⚠️ {gpsError} — Menggunakan lokasi default. Klik peta atau ikon crosshair untuk mengubah.
                        </span>
                      </div>
                    )}

                    <p className="text-[10px] text-slate-500 mt-1">* Klik peta untuk mengisi otomatis.</p>
                  </div>
                </div>

                <div className="min-h-[400px] w-full relative z-0 rounded-xl overflow-clip">
                  <MapContainer
                   center={[data.latitude, data.longitude]}
                   zoom={15}
                   scrollWheelZoom={true}
                   className="w-full h-full"
                 >
                   <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                   <MapController data={data} setData={setData} />
                 </MapContainer>
                </div>
              </div>
            </div>

            {/* BUTTONS */}
            <div className="flex gap-4 pt-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-300 border border-slate-600 hover:bg-slate-800 transition-all uppercase tracking-widest"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white py-3 rounded-xl font-bold text-sm uppercase tracking-widest shadow-lg shadow-cyan-900/20 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? "Menyimpan..." : "Simpan Data"}
              </button>
            </div>

          </form>
        </main>
      </div>
    </>
  );
};

